<script lang="ts">
  import {STEP} from '@/types/common';
  import {getContext, onDestroy, onMount} from 'svelte';
  import {getInstance} from '@/services/animation';
  import {project, step, download, preview} from '@/store';
  import {appEvents, ProjectService, RenderService} from '@/services';
  import {TopNav, Container, VideoControls, PreviewSidebar} from '@components';
  import PreviewArea from './preview/PreviewArea.svelte';
  import loadingStore from './preview/loading-store';
  import editorStore from '../lib/editor/editor.store';
  import EditorStyleView from '../lib/editor/StyleView/EditorStyleView.svelte';
  import MenuBar from '../lib/editor/MenuBar/MenuBar.svelte';
  import {fly} from 'svelte/transition';
  import ActiveElementToolbar from '../lib/editor/ActiveElementToolbar/ActiveElementToolbar.svelte';
  import SafeZoneButtons from '../lib/editor/SafeZone/SafeZoneButtons.svelte';
  import appConfig from '../config';
  import NewPreviewSidebar from '../components/new-preview-sidebar/NewPreviewSidebar.svelte';
  import ExtendedOptionMenu from '../components/new-preview-sidebar/ExtendedOptionMenu.svelte';
  import PopupBox from '../components/new-preview-sidebar/PopupBox/PopupBox.svelte';
  import {isBetaUser} from '../lib/beta/beta';

  let progressBar: any;
  let zoomPercent = 1;
  let currentProgress: number = 0;
  const animationService = getInstance();
  // const { open } = getContext("simple-modal");

  const onChange = (event: CustomEvent) => {
    try {
      animationService.pause();
      animationService.seek(event.detail.value / 100);
      animationService.seekVideo();
    } catch (err) {
      console.error('Could not proceed with onChange event', err);
    }
  };

  const playVideo = () => animationService.play();
  const pauseVideo = () => animationService.pause();

  const stopVideo = () => {
    try {
      animationService.pause();
      animationService.restartTimeline();
      animationService.seekVideo();
    } catch (err) {
      console.error('Could not stop video', err);
    }
  };

  const updateNextStep = () => step.update(STEP.RENDER);
  const updatePrevStep = () => step.update(STEP.SELECT_SUBTITLES);

  const saveProject = async (e: CustomEvent) => {
    const projectService = new ProjectService();
    const renderService = new RenderService();
    if (!$project.video) throw 'Video undefined.';
    if (!$project.template) throw 'Template undefined.';
    const projectData = await projectService.fetchSingle(appConfig.editorSkip);
    const qualityPreset = e.detail.qualityPreset || 'default';
    renderService.setup(animationService, projectData, qualityPreset).output();
    renderService.attachEventListeners({
      onBegin: () => {
        // open(
        //   Download,
        //   {},
        //   {
        //     closeButton: CloseButton,
        //   }
        // );
      },
      onProgress: notification => {
        const totalFrames = animationService.duration * 40;
        const currentFrame = notification.data.frame;

        if (currentFrame) {
          let progressRaw = (currentFrame / totalFrames) * 100;

          if (progressRaw > 100) {
            progressRaw = 100;
          }

          const progress = progressRaw;
          download.updateProgress(progress);
        }
      },
      onComplete: () => {
        download.updateProgress(100);
        download.complete(renderService.currentRequestId);
      }
    });
    renderService.sendToEncoder($project.template, $project.video);
  };

  let render = false;
  let beta: boolean = false;
  let betaLoaded: boolean = false;
  onMount(async () => {
    beta = await isBetaUser();
    betaLoaded = true;
    const projectService = new ProjectService();

    const projectData = await projectService.fetchSingle(appConfig.editorSkip);
    setTimeout(() => {
      project.set({
        changes: [],
        selectedSentences: projectData.sentences,
        ...projectData,
        template: {
          content: projectData.template
        }
      });
      // render = true;

      appEvents.trigger('cut-video', {
        status: 'completed',
        video: $project.video
      });
      render = true;
    }, 2000);
    loadingStore.set(false);
    animationService.onUpdate(progress => {
      try {
        if (progressBar) {
          let bar = progressBar.getProgressElement();
          bar.value = progress;
          progressBar.changeRunnerColor(bar);
        }
        currentProgress = progress;
      } catch (err) {
        console.warn('Could not update progress bar', err);
      }
    });
  });

  onDestroy(() => {
    animationService.clearUpdate();
  });
</script>

<TopNav
  step={4}
  label="Edit Video"
  hasPrevious={true}
  hasPreview={true}
  hasDownload={true}
  on:next={updateNextStep}
  on:prev={updatePrevStep}
  on:download={saveProject}
/>
{#if $project.video !== null}
  <Container className="flex overflow-none">
    {#if $editorStore.stylebarActive}
      <div class="relative flex w-3/12 flex-col gap-6 bg-slate-400 bg-opacity-10">
        <EditorStyleView />
      </div>
    {:else if betaLoaded}
      {#if !beta}
        <PreviewSidebar loading={$loadingStore.loading} />
      {:else}
        <NewPreviewSidebar />
      {/if}
    {/if}
    <div class="relative h-full flex-grow overflow-hidden" id="mainArea">
      <ExtendedOptionMenu />
      <PreviewArea {zoomPercent} />
      <div in:fly={{y: 100, duration: 300}} out:fly={{y: 100, duration: 300}} class="w-full flex-shrink-0">
        <div class="flex flex-col">
          <!-- TODO: for future reference on zoom implementation -->
          <!-- <VideoTools on:zoom-template={zoomTemplate} /> -->
          <VideoControls
            bind:progressBar
            showDuration={true}
            on:play-video={playVideo}
            on:stop-video={stopVideo}
            on:pause-video={pauseVideo}
            on:seek-timeline={onChange}
            progress={currentProgress}
            duration={$preview.totalDuration}
          />
        </div>
      </div>
    </div>
  </Container>
{/if}

<style lang="postcss" global>
  #mainArea {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-template-rows: calc(100% - 65px);
    @apply bg-gray-300;
  }
  .container-div {
    @apply flex flex-grow flex-col items-center justify-center;
  }

  .highlighted-element {
    box-shadow: inset 0 0 0 2px #1c73f7 !important;
  }
</style>
